import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useReadings } from "@/api/reseller/queries";
import type { Reading } from "@/api/reseller/types";
import {
	type Column,
	MeterTable,
	usePagination,
} from "@/components/meters/meter-table";
import { RowDetailsButton } from "@/components/table";
import { Badge } from "@/components/ui/badge";
import { useLocalizedNavigate } from "@/hooks/use-localized-navigate";

export function DistrictReadingsSection({
	districtId,
}: {
	districtId: number;
}) {
	const { t, i18n } = useTranslation();
	const { page, setPage } = usePagination();
	const [pageSize, setPageSize] = useState(20);
	const navigate = useLocalizedNavigate();

	const { data, isLoading } = useReadings({
		district_id: districtId,
		page,
		page_size: pageSize,
	});

	const columns: Column<Reading>[] = [
		{
			key: "reading_date",
			label: t("mqttReadingsTable.colReadingDate"),
			render: (row) =>
				new Date(row.reading_date).toLocaleString(i18n.language, {
					dateStyle: "short",
					timeStyle: "short",
				}),
		},
		{
			key: "mqtt_source",
			label: t("mqttReadingsTable.colMqttSource"),
			render: (row) => (
				<span className="font-mono text-sm">
					{row.type === "mqtt"
						? (row.mqtt_source?.sn ?? "—")
						: (row.advant_source?.sn ?? "—")}
				</span>
			),
		},
		{
			key: "completed",
			label: t("mqttReadingsTable.colStatus"),
			render: (row) =>
				row.completed ? (
					<Badge variant="success">
						{t("mqttReadingsTable.statusCompleted")}
					</Badge>
				) : (
					<Badge variant="info">
						{t("mqttReadingsTable.statusInProgress")}
					</Badge>
				),
		},
		{
			key: "meter_readings_count",
			label: t("mqttReadingsTable.colReadingCount"),
		},
		{
			key: "_actions",
			label: "",
			render: (row) => (
				<RowDetailsButton
					onClick={() => navigate("readings", String(row.id))}
				/>
			),
		},
	];

	return (
		<div className="flex min-h-0 flex-1 flex-col gap-2">
			<h3 className="text-base font-semibold">
				{t("districts.readingsTitle")}
			</h3>
			<MeterTable
				data={data}
				columns={columns}
				loading={isLoading}
				page={page}
				pageSize={pageSize}
				onPageChange={setPage}
				onPageSizeChange={(size) => {
					setPageSize(size);
					setPage(1);
				}}
			/>
		</div>
	);
}
